import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useBalances, useHistory, useMyParticipants } from "../lib/queries";
import { ErrorBox, Loading } from "../components/Loading";
import { fmtDate } from "../lib/format";

type Row = {
  id: string;
  status: "confirmed" | "cancelled" | "attended" | "noshow";
  needs_skipass: boolean;
  participant: { id: string; name: string };
  session: {
    id: string;
    starts_at: string;
    location_text: string | null;
    activity_type: { name: string; emoji: string | null };
  };
};

export default function History() {
  const { user } = useAuth();
  const historyQ = useHistory(user?.parent_id ?? undefined);
  const kidsQ = useMyParticipants(user?.parent_id ?? undefined);

  const grouped = useMemo(() => {
    const out = new Map<string, Row[]>(); // kid_id → rows (sorted)
    for (const r of (historyQ.data ?? []) as unknown as Row[]) {
      const k = r.participant.id;
      if (!out.has(k)) out.set(k, []);
      out.get(k)!.push(r);
    }
    for (const arr of out.values()) {
      arr.sort((a, b) => b.session.starts_at.localeCompare(a.session.starts_at));
    }
    return out;
  }, [historyQ.data]);

  if (historyQ.isLoading) return <Loading />;
  if (historyQ.error) return <ErrorBox message={(historyQ.error as Error).message} />;

  const kids = kidsQ.data ?? [];

  return (
    <div className="p-4 space-y-5">
      <h1 className="text-2xl font-bold">Історія</h1>

      {kids.length === 0 && (
        <div className="card text-center">
          <p className="text-tg-hint text-sm mb-3">Немає дітей у профілі.</p>
          <Link to="/me/kids" className="btn-primary inline-flex">+ Додати дитину</Link>
        </div>
      )}

      {kids.map((kid) => (
        <section key={kid.id} className="space-y-2">
          <KidHeader id={kid.id} name={kid.name} totalRecords={grouped.get(kid.id)?.length ?? 0} />
          <KidTable rows={grouped.get(kid.id) ?? []} />
        </section>
      ))}
    </div>
  );
}

function KidHeader({ id, name, totalRecords }: { id: string; name: string; totalRecords: number }) {
  const balancesQ = useBalances(id);
  const remaining = (balancesQ.data ?? []).reduce((sum, b) => sum + b.remaining, 0);
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-lg font-semibold">{name}</h2>
      <div className="text-tg-hint text-sm">
        {remaining > 0 && <span className="mr-2">Залишилось: {remaining}</span>}
        <span>Записів: {totalRecords}</span>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<Row["status"], string> = {
  attended: "✓ був",
  noshow: "✗ не прийшов",
  cancelled: "— скасовано",
  confirmed: "запис",
};

const STATUS_CLASS: Record<Row["status"], string> = {
  attended: "text-green-700",
  noshow: "text-red-700",
  cancelled: "text-tg-hint line-through",
  confirmed: "text-tg-link",
};

function KidTable({ rows }: { rows: Row[] }) {
  if (rows.length === 0) {
    return <div className="text-tg-hint text-sm">Поки немає записів.</div>;
  }
  return (
    <ul className="card divide-y divide-tg-bg/40 -my-1">
      {rows.map((r) => (
        <li key={r.id} className="py-2 flex items-baseline justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm">
              <span className="mr-1">{r.session.activity_type.emoji}</span>
              {r.session.activity_type.name}
              {r.needs_skipass && <span className="ml-1">🎫</span>}
            </div>
            {r.session.location_text && (
              <div className="text-tg-hint text-xs">{r.session.location_text}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm tabular-nums">{fmtDate(r.session.starts_at)}</div>
            <div className={`text-xs ${STATUS_CLASS[r.status]}`}>{STATUS_LABEL[r.status]}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}
